/** Financial Reports page - income statement, balance sheet, and financial analytics. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Refresh,
  Download,
  PictureAsPdf,
  TableChart,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { billingApi } from "../api/client";

interface RevenueByDestination {
  destination: string;
  calls: number;
  minutes: number;
  revenue: number;
}

interface ExpenseByCategory {
  category: string;
  amount: number;
  percentage: number;
}

interface IncomeStatement {
  revenue: {
    total: number;
    breakdown: RevenueByDestination[];
  };
  direct_costs: {
    carrier_cost: number;
    taxes: number;
    other: number;
    total: number;
  };
  gross_profit: number;
  operating_expenses: {
    salaries: number;
    software: number;
    infrastructure: number;
    other: number;
    total: number;
  };
  operating_income: number;
  other_income: number;
  net_income: number;
  margin_percent: number;
}

/** Financial Reports page component. */
const FinancialReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"income" | "balance" | "cash-flow">("income");
  const [reportPeriod, setReportPeriod] = useState("last_month");
  const [loading, setLoading] = useState(false);
  const [incomeStatement, setIncomeStatement] = useState<IncomeStatement | null>(null);

  // Mock income statement data - in production, this would come from the backend API
  // The backend would aggregate this from invoices, CDRs, and expense records
  const mockIncomeStatement: IncomeStatement = {
    revenue: {
      total: 156479.00,
      breakdown: [
        { destination: "India", calls: 15234, minutes: 89456, revenue: 45600.00 },
        { destination: "Pakistan", calls: 12456, minutes: 67234, revenue: 38200.00 },
        { destination: "Bangladesh", calls: 10234, minutes: 56789, revenue: 29800.00 },
        { destination: "Nigeria", calls: 8901, minutes: 45678, revenue: 25100.00 },
        { destination: "Philippines", calls: 7845, minutes: 34567, revenue: 17779.00 },
      ],
    },
    direct_costs: {
      carrier_cost: 98500.00,
      taxes: 12458.32,
      other: 2340.00,
      total: 113298.32,
    },
    gross_profit: 43180.68,
    operating_expenses: {
      salaries: 15000.00,
      software: 3500.00,
      infrastructure: 5200.00,
      other: 1800.00,
      total: 25500.00,
    },
    operating_income: 17680.68,
    other_income: 450.00,
    net_income: 18130.68,
    margin_percent: 11.59,
  };

  useEffect(() => {
    // Simulate loading data
    setLoading(true);
    setTimeout(() => {
      setIncomeStatement(mockIncomeStatement);
      setLoading(false);
    }, 500);
  }, [reportPeriod]);

  const handleRefresh = () => {
    setLoading(true);
    // In production, fetch actual financial data from API
    // GET /api/v1/billing/financial-reports?period=last_month
    setTimeout(() => {
      setIncomeStatement(mockIncomeStatement);
      setLoading(false);
    }, 500);
  };

  const handleExportPDF = () => {
    // In production, this would call the backend to generate a PDF
    alert("PDF export will be generated from the backend API.");
  };

  const handleExportCSV = () => {
    // Generate CSV export for income statement
    if (!incomeStatement) return;

    const rows = [
      ["REVENUE"],
      [`Total Revenue,${incomeStatement.revenue.total.toFixed(2)}`],
      [""],
      ["DIRECT COSTS"],
      [`Carrier Cost,${incomeStatement.direct_costs.carrier_cost.toFixed(2)}`],
      [`Taxes,${incomeStatement.direct_costs.taxes.toFixed(2)}`],
      [`Other,${incomeStatement.direct_costs.other.toFixed(2)}`],
      [`Total Direct Costs,${incomeStatement.direct_costs.total.toFixed(2)}`],
      [""],
      [`GROSS PROFIT,${incomeStatement.gross_profit.toFixed(2)}`],
      [""],
      ["OPERATING EXPENSES"],
      [`Salaries,${incomeStatement.operating_expenses.salaries.toFixed(2)}`],
      [`Software,${incomeStatement.operating_expenses.software.toFixed(2)}`],
      [`Infrastructure,${incomeStatement.operating_expenses.infrastructure.toFixed(2)}`],
      [`Other,${incomeStatement.operating_expenses.other.toFixed(2)}`],
      [`Total Operating Expenses,${incomeStatement.operating_expenses.total.toFixed(2)}`],
      [""],
      [`OPERATING INCOME,${incomeStatement.operating_income.toFixed(2)}`],
      [`Other Income,${incomeStatement.other_income.toFixed(2)}`],
      [""],
      [`NET INCOME,${incomeStatement.net_income.toFixed(2)}`],
      [`Profit Margin,${incomeStatement.margin_percent.toFixed(2)}%`],
    ];

    const csvContent = rows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `income-statement-${reportPeriod}-${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderIncomeStatement = () => {
    if (!incomeStatement) return null;

    return (
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" color="success.main" fontWeight="bold">
                    ${incomeStatement.revenue.total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Gross Profit
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    ${incomeStatement.gross_profit.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {((incomeStatement.gross_profit / incomeStatement.revenue.total) * 100).toFixed(1)}% margin
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Operating Income
                  </Typography>
                  <Typography variant="h5" color="info.main" fontWeight="bold">
                    ${incomeStatement.operating_income.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Net Income
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" color={incomeStatement.net_income >= 0 ? "success.main" : "error.main"} fontWeight="bold">
                      ${incomeStatement.net_income.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </Typography>
                    {incomeStatement.net_income >= 0 ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {incomeStatement.margin_percent.toFixed(1)}% margin
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Income Statement Table */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Income Statement
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          REVENUE
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold">
                          ${incomeStatement.revenue.total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          DIRECT COSTS
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Carrier Cost</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.direct_costs.carrier_cost.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Taxes</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.direct_costs.taxes.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Other</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.direct_costs.other.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ pl: 4 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Total Direct Costs
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold">
                          (${incomeStatement.direct_costs.total.toLocaleString("en-US", { minimumFractionDigits: 2 })})
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          GROSS PROFIT
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                          ${incomeStatement.gross_profit.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          OPERATING EXPENSES
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Salaries</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.operating_expenses.salaries.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Software</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.operating_expenses.software.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Infrastructure</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.operating_expenses.infrastructure.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Other</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.operating_expenses.other.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ pl: 4 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Total Operating Expenses
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold">
                          (${incomeStatement.operating_expenses.total.toLocaleString("en-US", { minimumFractionDigits: 2 })})
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          OPERATING INCOME
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold" color="info.main">
                          ${incomeStatement.operating_income.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Other Income</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        ${incomeStatement.other_income.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ bgcolor: "success.light", "&:hover": { bgcolor: "success.light" } }}>
                      <TableCell colSpan={2}>
                        <Typography variant="h6" fontWeight="bold">
                          NET INCOME
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" fontWeight="bold" color="success.dark">
                          ${incomeStatement.net_income.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue by Destination */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue by Destination
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Destination</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incomeStatement.revenue.breakdown.map((item) => (
                      <TableRow key={item.destination}>
                        <TableCell>{item.destination}</TableCell>
                        <TableCell align="right">
                          ${item.revenue.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">
                          {((item.revenue / incomeStatement.revenue.total) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderBalanceSheet = () => {
    return (
      <Alert severity="info">
        Balance Sheet report will be implemented with asset and liability tracking in a future update.
        This requires accounts receivable, accounts payable, and balance tracking functionality.
      </Alert>
    );
  };

  const renderCashFlow = () => {
    return (
      <Alert severity="info">
        Cash Flow statement will be implemented with payment tracking and cash flow analysis in a future update.
        This requires integration with payment gateway transaction history.
      </Alert>
    );
  };

  const getDateRangeLabel = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    switch (reportPeriod) {
      case "this_month":
        return `${format(now, "MMMM yyyy")}`;
      case "last_month":
        return `${format(lastMonth, "MMMM yyyy")}`;
      case "this_quarter":
        return `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;
      case "this_year":
        return `${now.getFullYear()}`;
      default:
        return "Select period";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Financial Reports
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={reportPeriod}
              label="Period"
              onChange={(e) => setReportPeriod(e.target.value)}
            >
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="this_quarter">This Quarter</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChart />}
            onClick={handleExportCSV}
            disabled={!incomeStatement}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleExportPDF}
            disabled={!incomeStatement}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Tabs for report types */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Income Statement" value="income" />
          <Tab label="Balance Sheet" value="balance" />
          <Tab label="Cash Flow" value="cash-flow" />
        </Tabs>
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Financial reports are generated from invoice transactions, CDR billing data, and expense records.
        Reports are updated in real-time as invoices are generated and payments are processed.
      </Alert>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === "income" && renderIncomeStatement()}
          {activeTab === "balance" && renderBalanceSheet()}
          {activeTab === "cash-flow" && renderCashFlow()}
        </>
      )}
    </Container>
  );
};

export default FinancialReports;
